import React, { useEffect, useState } from 'react';
import api from '../../api/client';

interface CallLog {
  call_id: string;
  caller_number: string;
  duration_seconds?: number;
  status: 'completed' | 'failed' | 'in-progress' | string;
  agent_name?: string;
  created_at: string;
  transcript?: string;
}

type DateFilter = 'today' | '7d' | '30d';

function StatusBadge({ status }: { status: string }) {
  const s = status?.toLowerCase();
  if (s === 'completed') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        Completed
      </span>
    );
  }
  if (s === 'failed') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
        Failed
      </span>
    );
  }
  if (s === 'in-progress') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
        In Progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(107,114,128,0.2)', color: '#9ca3af' }}>
      {status}
    </span>
  );
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const CallLogs: React.FC = () => {
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilter>('7d');
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        let since: Date;
        if (dateFilter === 'today') {
          since = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (dateFilter === '7d') {
          since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else {
          since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        }
        const res = await api.get('/api/v1/call-logs', {
          params: { since: since.toISOString(), limit: 100 },
        });
        const data = res.data;
        setCalls(data.calls || data.data || data || []);
      } catch (err: any) {
        setError('Unable to load call logs. Please try again later.');
        setCalls([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [dateFilter]);

  const filterLabels: Record<DateFilter, string> = { // eslint-disable-line @typescript-eslint/no-unused-vars
    today: 'Today',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
  };

  return (
    <div className="p-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Call Logs</h1>
          <p className="text-gray-400 mt-1">Review your recent calls and transcripts.</p>
        </div>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value as DateFilter)}
          className="rounded-lg px-3 py-2 text-sm text-gray-200 outline-none cursor-pointer"
          style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.12)', color: '#d1d5db' }}
        >
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>

      {/* Content */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Loading call logs...</p>
          </div>
        </div>
      )}

      {!loading && error && (
        <div
          className="rounded-xl p-6 text-center"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <div className="text-3xl mb-3">⚠️</div>
          <p className="text-red-300 font-medium">{error}</p>
        </div>
      )}

      {!loading && !error && calls.length === 0 && (
        <div
          className="rounded-xl p-12 text-center"
          style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="text-5xl mb-4">📞</div>
          <h3 className="text-white font-semibold text-lg mb-2">No calls yet</h3>
          <p className="text-gray-400 text-sm max-w-sm mx-auto">
            Assign a phone number to your agent to start receiving calls. Your call history will appear here.
          </p>
        </div>
      )}

      {!loading && !error && calls.length > 0 && (
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {/* Table header */}
          <div
            className="grid text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3"
            style={{
              gridTemplateColumns: '1fr 100px 120px 140px 160px 120px',
              background: 'rgba(255,255,255,0.03)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <span>Caller</span>
            <span>Duration</span>
            <span>Status</span>
            <span>Agent</span>
            <span>Time</span>
            <span></span>
          </div>

          {/* Rows */}
          {calls.map((call, i) => (
            <div
              key={call.call_id || i}
              className="grid items-center px-5 py-3.5 text-sm transition-colors duration-100"
              style={{
                gridTemplateColumns: '1fr 100px 120px 140px 160px 120px',
                borderBottom: i < calls.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              <span className="text-white font-medium">{call.caller_number || '—'}</span>
              <span className="text-gray-300">{formatDuration(call.duration_seconds)}</span>
              <span><StatusBadge status={call.status} /></span>
              <span className="text-gray-300 truncate">{call.agent_name || '—'}</span>
              <span className="text-gray-400 text-xs">{new Date(call.created_at).toLocaleString()}</span>
              <span>
                {call.transcript && (
                  <button
                    onClick={() => setSelectedCall(call)}
                    className="text-xs px-3 py-1 rounded-lg transition-colors"
                    style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.2)' }}
                  >
                    Transcript
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Transcript modal */}
      {selectedCall && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setSelectedCall(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
            style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.12)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold">Call Transcript</h3>
                <p className="text-gray-400 text-xs mt-0.5">{selectedCall.caller_number} · {new Date(selectedCall.created_at).toLocaleString()}</p>
              </div>
              <button
                onClick={() => setSelectedCall(null)}
                className="text-gray-400 hover:text-white text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div
              className="rounded-lg p-4 text-sm text-gray-300 whitespace-pre-wrap leading-relaxed"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              {selectedCall.transcript}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
